/**
 * Navigation Configuration
 *
 * Centralized navigation configuration for all user roles.
 * Provides consistent navigation structure across the application.
 */

import type { ReactElement } from 'react';
import {
  BarChart3,
  ClipboardList,
  Folder,
  Users,
  CheckSquare,
  FileText,
  Award,
  Settings,
  UserCog,
  DollarSign,
  Factory,
  ScrollText,
  Search,
} from 'lucide-react';
import { UserPermission, UserRole } from '../types/user';

/**
 * Navigation Item Interface
 */
export interface NavigationItem {
  /** Unique identifier for the navigation item */
  id: string;
  /** Display label for the item */
  label: string;
  /** Icon component (Lucide React icon) */
  icon?: ReactElement;
  /** Route path */
  href?: string;
  /** Badge to display (count or text) */
  badge?: string | number;
  /** Child navigation items (for nested menus) */
  children?: NavigationItem[];
  /** Add separator line after this item */
  separator?: boolean;
  /** Required permissions (user needs at least one) */
  permissions?: UserPermission[];
  /** Required roles (user needs at least one) */
  roles?: UserRole[];
}

/**
 * Navigation Configuration Interface
 */
export interface NavigationConfig {
  company: NavigationItem[];
  consultant: NavigationItem[];
  auditor: NavigationItem[];
  admin: NavigationItem[];
}

/**
 * Main Navigation Configuration
 */
export const navigationConfig: NavigationConfig = {
  // Company Navigation
  company: [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <BarChart3 className="w-5 h-5" />,
      href: '/company/dashboard',
    },
    {
      id: 'applications',
      label: 'Applications',
      icon: <ClipboardList className="w-5 h-5" />,
      href: '/company/applications',
      permissions: [UserPermission.CREATE_APPLICATION, UserPermission.SUBMIT_APPLICATION],
    },
    {
      id: 'documents',
      label: 'Documents',
      icon: <Folder className="w-5 h-5" />,
      href: '/company/documents',
      permissions: [UserPermission.VIEW_DOCUMENTS],
    },
    {
      id: 'suppliers',
      label: 'Suppliers',
      icon: <Factory className="w-5 h-5" />,
      href: '/company/suppliers',
      permissions: [UserPermission.VIEW_DOCUMENTS],
    },
    {
      id: 'payments',
      label: 'Payments',
      icon: <DollarSign className="w-5 h-5" />,
      href: '/company/payments',
      permissions: [UserPermission.VIEW_PAYMENT],
      separator: true,
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings className="w-5 h-5" />,
      href: '/company/settings',
    },
  ],

  // Consultant Navigation
  consultant: [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <BarChart3 className="w-5 h-5" />,
      href: '/consultant/dashboard',
    },
    {
      id: 'clients',
      label: 'My Clients',
      icon: <Users className="w-5 h-5" />,
      href: '/consultant/clients',
      permissions: [UserPermission.VIEW_DOCUMENTS, UserPermission.REVIEW_APPLICATION],
    },
    {
      id: 'pre-audits',
      label: 'Pre-Audits',
      icon: <CheckSquare className="w-5 h-5" />,
      href: '/consultant/pre-audits',
      permissions: [UserPermission.CONDUCT_AUDIT, UserPermission.SCHEDULE_AUDIT],
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: <FileText className="w-5 h-5" />,
      href: '/consultant/reports',
      permissions: [UserPermission.REVIEW_APPLICATION],
      children: [
        {
          id: 'car-generation',
          label: 'CAR Generation',
          href: '/consultant/reports/car',
        },
        {
          id: 'consultant-reports',
          label: 'Consultant Reports',
          href: '/consultant/reports/list',
        },
      ],
    },
    {
      id: 'invoices',
      label: 'Invoices',
      icon: <ScrollText className="w-5 h-5" />,
      href: '/consultant/invoices',
      permissions: [UserPermission.VIEW_PAYMENT],
      separator: true,
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings className="w-5 h-5" />,
      href: '/consultant/settings',
    },
  ],

  // Auditor Navigation
  auditor: [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <BarChart3 className="w-5 h-5" />,
      href: '/auditor/dashboard',
    },
    {
      id: 'applications',
      label: 'Application Queue',
      icon: <ClipboardList className="w-5 h-5" />,
      href: '/auditor/applications',
      permissions: [UserPermission.REVIEW_APPLICATION],
    },
    {
      id: 'audits',
      label: 'Audit Checklist',
      icon: <CheckSquare className="w-5 h-5" />,
      href: '/auditor/audits',
      permissions: [UserPermission.CONDUCT_AUDIT],
    },
    {
      id: 'ncr',
      label: 'NCR Management',
      icon: <FileText className="w-5 h-5" />,
      href: '/auditor/ncr',
      permissions: [UserPermission.CONDUCT_AUDIT, UserPermission.FINALIZE_AUDIT],
    },
    {
      id: 'certificates',
      label: 'Certificates',
      icon: <Award className="w-5 h-5" />,
      href: '/auditor/certificates',
      permissions: [UserPermission.VIEW_CERTIFICATE, UserPermission.ISSUE_CERTIFICATE],
      separator: true,
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings className="w-5 h-5" />,
      href: '/auditor/settings',
    },
  ],

  // Admin Navigation
  admin: [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <BarChart3 className="w-5 h-5" />,
      href: '/admin/dashboard',
    },
    {
      id: 'users',
      label: 'User Management',
      icon: <UserCog className="w-5 h-5" />,
      href: '/admin/users',
      permissions: [UserPermission.MANAGE_USERS],
    },
    {
      id: 'applications',
      label: 'Applications',
      icon: <ClipboardList className="w-5 h-5" />,
      href: '/admin/applications',
      permissions: [UserPermission.REVIEW_APPLICATION, UserPermission.APPROVE_APPLICATION],
    },
    {
      id: 'certificates',
      label: 'Certificates',
      icon: <Award className="w-5 h-5" />,
      href: '/admin/certificates',
      permissions: [UserPermission.VIEW_CERTIFICATE, UserPermission.ISSUE_CERTIFICATE],
    },
    {
      id: 'payments',
      label: 'Payment Records',
      icon: <DollarSign className="w-5 h-5" />,
      href: '/admin/payments',
      permissions: [UserPermission.VIEW_PAYMENT, UserPermission.PROCESS_PAYMENT],
    },
    {
      id: 'audits',
      label: 'Audit Oversight',
      icon: <Search className="w-5 h-5" />,
      href: '/admin/audits',
      permissions: [UserPermission.CONDUCT_AUDIT, UserPermission.FINALIZE_AUDIT],
      separator: true,
    },
    {
      id: 'settings',
      label: 'System Settings',
      icon: <Settings className="w-5 h-5" />,
      href: '/admin/settings',
      permissions: [UserPermission.MANAGE_SYSTEM],
    },
  ],
};

/**
 * Get navigation items for a specific role
 *
 * @param role - User role (company, consultant, auditor, admin)
 * @returns Array of navigation items for the role
 */
export function getNavigationByRole(
  role: 'company' | 'consultant' | 'auditor' | 'admin'
): NavigationItem[] {
  return navigationConfig[role] || [];
}

/**
 * Detect user role from current route path
 *
 * @param path - Current route path (e.g., '/company/dashboard')
 * @returns Detected role or null if not found
 */
export function getRoleFromPath(
  path: string
): 'company' | 'consultant' | 'auditor' | 'admin' | null {
  const normalizedPath = path.toLowerCase();

  if (normalizedPath.startsWith('/company')) return 'company';
  if (normalizedPath.startsWith('/consultant')) return 'consultant';
  if (normalizedPath.startsWith('/auditor')) return 'auditor';
  if (normalizedPath.startsWith('/admin')) return 'admin';

  return null;
}

/**
 * Check if a navigation item is active based on current path
 *
 * @param item - Navigation item to check
 * @param currentPath - Current route path
 * @returns True if item is active
 */
export function isNavItemActive(
  item: NavigationItem,
  currentPath: string
): boolean {
  if (!item.href) return false;

  // Special case: Dashboard should only match exact path
  if (item.id === 'dashboard') {
    return currentPath === item.href;
  }

  // Exact match
  if (currentPath === item.href) return true;

  // Starts with (for nested routes)
  if (currentPath.startsWith(item.href + '/')) return true;

  // Check children
  if (item.children) {
    return item.children.some((child) => isNavItemActive(child, currentPath));
  }

  return false;
}

/**
 * Flatten navigation structure (useful for breadcrumbs)
 *
 * @param items - Navigation items
 * @returns Flattened array of all navigation items
 */
export function flattenNavigation(items: NavigationItem[]): NavigationItem[] {
  const result: NavigationItem[] = [];

  const flatten = (navItems: NavigationItem[]) => {
    navItems.forEach((item) => {
      result.push(item);
      if (item.children) {
        flatten(item.children);
      }
    });
  };

  flatten(items);
  return result;
}

/**
 * Find a navigation item by ID
 *
 * @param items - Navigation items to search
 * @param id - Item ID to find
 * @returns Navigation item or null if not found
 */
export function findNavItemById(
  items: NavigationItem[],
  id: string
): NavigationItem | null {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.children) {
      const found = findNavItemById(item.children, id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Filter navigation items based on user permissions and roles.
 * Items without permissions/roles are always shown.
 * Items with permissions require at least one matching permission.
 * Items with roles require at least one matching role.
 */
export function filterNavigationByAccess(
  items: NavigationItem[],
  userPermissions: UserPermission[],
  userRole: UserRole | undefined
): NavigationItem[] {
  return items.filter((item) => {
    // If no restrictions, always show
    if (!item.permissions && !item.roles) return true;

    // Check role restriction
    if (item.roles && item.roles.length > 0) {
      if (!userRole || !item.roles.includes(userRole)) return false;
    }

    // Check permission restriction (user needs at least one)
    if (item.permissions && item.permissions.length > 0) {
      const hasAny = item.permissions.some((p) => userPermissions.includes(p));
      if (!hasAny) return false;
    }

    return true;
  }).map((item) => {
    // Recursively filter children
    if (item.children) {
      return {
        ...item,
        children: filterNavigationByAccess(item.children, userPermissions, userRole),
      };
    }
    return item;
  });
}
